import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import cx from 'clsx';
import styles from './carousel.module.css';
import { Dots } from './dots.tsx';

type EmblaCarouselOptions = Parameters<typeof useEmblaCarousel>[0];

export type CarouselOptions = EmblaCarouselOptions & {
  autoplay?: AutoplayOptionsType | boolean;
};

export interface CarouselActions {
  scrollPrev: () => void;
  scrollNext: () => void;
}

interface CarouselProps {
  children: React.ReactNode;
  slideSize?: number | string;
  slideSpacing?: number | string;
  options?: CarouselOptions;
  onIndexChange?: (index: number) => void;
  hideDots?: boolean;
  dotsPosition?: 'top' | 'bottom';
  viewportClassName?: string;
  containerClassName?: string;
  dotsClassName?: string;
}

export const Carousel = React.forwardRef(function Carousel(
  {
    children,
    slideSize = '100%',
    slideSpacing = '1rem',
    options = {
      align: 'center',
    },
    hideDots = false,
    onIndexChange,
    dotsPosition = 'top',
    viewportClassName,
    containerClassName,
    dotsClassName,
  }: CarouselProps,
  ref: React.Ref<CarouselActions>
): React.JSX.Element {
  const plugins = [];
  if (options.autoplay) {
    const autoplayConfig = options.autoplay === true ? {} : options.autoplay;
    plugins.push(Autoplay(autoplayConfig));
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  const [index, setIndex] = React.useState(0);
  const slidesCount = React.Children.toArray(children).length;

  React.useEffect(() => {
    onIndexChange?.(index);
  }, [index, onIndexChange]);

  React.useEffect(() => {
    if (!emblaApi) return;

    const handleScroll = (): void => {
      setIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('scroll', handleScroll);
    emblaApi.on('init', handleScroll);

    return () => {
      emblaApi.off('scroll', handleScroll);
      emblaApi.off('init', handleScroll);
    };
  }, [emblaApi]);

  // Expose scrollPrev and scrollNext methods via ref
  React.useImperativeHandle(ref, () => ({
    scrollPrev: () => emblaApi?.scrollPrev(),
    scrollNext: () => emblaApi?.scrollNext(),
  }));

  return (
    <>
      {!hideDots && dotsPosition === 'top' ? (
        <Dots className={dotsClassName} count={slidesCount} index={index} />
      ) : null}

      <div
        className={cx(styles.emblaViewport, viewportClassName)}
        ref={emblaRef}
        style={
          {
            '--slide-size': slideSize,
            '--slide-spacing': slideSpacing,
          } as React.CSSProperties
        }
      >
        <div className={cx(styles.emblaContainer, containerClassName)}>{children}</div>
      </div>

      {!hideDots && dotsPosition === 'bottom' ? (
        <Dots className={dotsClassName} count={slidesCount} index={index} />
      ) : null}
    </>
  );
});

export function CarouselItem({
  className,
  children,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return <div className={cx(styles.emblaSlide, className)}>{children}</div>;
}
